import LayoutPage from "components/LayoutPage/LayoutPage";
import React, {FC, useState} from "react";
import facebookSvg from "images/Facebook.svg";
import googleSvg from "images/Google.svg";
import githubSvg from "images/github.svg";
import Input from "components/Input/Input";
import ButtonPrimary from "components/Button/ButtonPrimary";
import NcLink from "components/NcLink/NcLink";
import {Helmet} from "react-helmet";
import {auth, facebookProvider, githubProvider, googleProvider, analytics} from "../../configs/firebase-config";
import {
  AuthProvider,
  browserSessionPersistence,
  fetchSignInMethodsForEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup
} from "firebase/auth";
import {logEvent} from "firebase/analytics"
import hidepwd from "../../images/hide-password.svg";
import showpwd from "../../images/show-password.svg";
import BgGlassmorphism from "../../components/BgGlassmorphism/BgGlassmorphism";
import {authChecks, displayToast, toastType} from "../../global/Functions";
import {useHistory} from "react-router";

export interface PageLoginProps {
  className?: string;
  setAuth:React.Dispatch<React.SetStateAction<boolean>>
}

const PageLogin: FC<PageLoginProps> = ({ className = "" }, {setAuth}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordShown, setPasswordShown] = useState(false);
  const history = useHistory();

  const providers: any = {
    google: googleProvider,
    facebook: facebookProvider,
    gitHub: githubProvider,
  };
  const loginSocials = [
    {
      name: "Continue with Facebook",
      href: "#",
      icon: facebookSvg,
      provider: providers.facebook,
    },
    {
      name: "Continue with Github",
      href: "#",
      icon: githubSvg,
      provider: providers.gitHub,
    },
    {
      name: "Continue with Google",
      href: "#",
      icon: googleSvg,
      provider: providers.google,
    },
  ];


  /*TODO: IMPLEMENT PROMPT FOR PASSWORD INPUT*/
  function promptUserForPassword() {
    return window.prompt("Enter password: ");
  }

  const handleSignIn = async (provider: AuthProvider) => {
    setPersistence(auth, browserSessionPersistence).then(async () => {
      signInWithPopup(auth, provider).then(async (userCredentials) => {
        const exists = await authChecks(userCredentials);
        displayToast("Logged In", toastType.success);
        await logEvent(analytics, 'login', {method: `${userCredentials.providerId}`});
        exists ? history.push("/dashboard") : history.push("/subscription");
      }).catch((error) => {
        if (error.code === "auth/account-exists-with-different-credential") {
          let email = error.customData.email;
          fetchSignInMethodsForEmail(auth, email).then((methods) => {
            if (methods[0] === "password") {
              const password = promptUserForPassword(); // TODO: implement promptUserForPassword.
              signInWithEmailAndPassword(auth, email, password!).then(async (userCredentials) => {
                displayToast("Logged In", toastType.success);
                const exists = await authChecks(userCredentials.user);
                exists ? history.push("/dashboard"): history.push("/subscription");
              });
              return;
            }
            displayToast("App registered with " + String([methods[0].split(".")[0]]), toastType.error);
          }).catch((error) => {
            displayToast(error.code, toastType.error);
          });
        }
      });
    })
  }

  const logIn = (e: any) => {
    e.preventDefault();
    setPersistence(auth, browserSessionPersistence).then(() => {
      signInWithEmailAndPassword(auth, email, password).then(async (userCredentials) => {
        displayToast("Logged In", toastType.success);
        const exists = await authChecks(userCredentials);
        exists ?  history.push("/dashboard") : history.push("/subscription");
      }).catch((error) => {
        //Implemented Error checks
        if(error.code === "auth/wrong-password") {
          displayToast("Wrong password", toastType.error);
        }else if(error.code === "auth/user-not-found"){
          displayToast(`No account registered using ${email}`, toastType.error);
        }else {
          displayToast(error.code, toastType.error);
        }
      })
    }).catch((error) => {
      displayToast(error.code, toastType.error);
    })
    e.target.reset();
  };

  return (
    <div className={`PageLogin ${className}`} data-id="PageLogin">
      <Helmet>
        <title>Login | CryptoVerse</title>
      </Helmet>
        <BgGlassmorphism />
      <LayoutPage
        subHeading="To The Cryptoverse"
        headingEmoji=""
        heading="Welcome Back"
      >
        <div className="max-w-md mx-auto space-y-6">
          <div className="grid gap-3">
            {loginSocials.map((item, index) => (
              <a
                key={index}
                href={item.href}
                onClick={() => handleSignIn(item.provider)}
                className="will-change-transform flex w-full rounded-lg bg-primary-50 dark:bg-neutral-800 px-4 py-3 transform transition-transform sm:px-6 hover:translate-y-[-2px]"
              >
                <img
                  className="flex-shrink-0"
                  src={item.icon}
                  alt={item.name}
                />
                <h3 className="flex-grow text-center text-sm font-medium text-neutral-700 dark:text-neutral-300 sm:text-sm">
                  {item.name}
                </h3>
              </a>
            ))}
          </div>
          {/* OR */}
          <div className="relative text-center">
            <span className="relative z-10 inline-block px-4 font-medium text-sm bg-white dark:text-neutral-400 dark:bg-neutral-900">
              OR
            </span>
            <div className="absolute left-0 w-full top-1/2 transform -translate-y-1/2 border border-neutral-100 dark:border-neutral-800"></div>
          </div>
          {/* FORM */}
          <form
            className="grid grid-cols-1 gap-6"
            action="#"
            method="post"
            onSubmit={logIn}
          >
            <label className="block">
              <span className="text-neutral-800 dark:text-neutral-200">
                Email address
              </span>
              <Input
                type="email"
                placeholder="name@example.com"
                className="mt-1"
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            <div className={"pwd-container"} style={{position: "relative"}}>
              <label className="block">
                <span className="flex justify-between items-center text-neutral-800 dark:text-neutral-200">
                  Password
                  <NcLink to="/forgot-pass" className="text-sm">
                    Forgot password?
                  </NcLink>
                </span>
                <Input
                  type={passwordShown ? "text" : "password"}
                  placeholder={passwordShown ? "Shhh! It's a secret...": "**********"}
                  className="mt-1"
                  onChange={(e) => setPassword(e.target.value)}
                />
              </label>
              {/* TODO: styling for icon*/}
              <img
                title={passwordShown ? "Hide password" : "Show password"}
                src={passwordShown ? hidepwd : showpwd}
                onClick={() =>{setPasswordShown(!passwordShown)}}
                style={{
                  cursor: "pointer",
                  position: "absolute",
                  width: "1.5rem",
                  right: "1.25rem",
                  top: "2.5rem",
                  fill: "white"
                }}
                alt="Show/Hide Password"
              />
            </div>

            <ButtonPrimary type="submit">Continue</ButtonPrimary>
          </form>
          <span className="block text-center text-neutral-700 dark:text-neutral-300">
            Don't have an account? {` `}
            <NcLink to="/signup">Sign Up</NcLink>
          </span>
        </div>
      </LayoutPage>
    </div>
  );
};

export default PageLogin;
